import type { i18n as I18nInstance } from 'i18next';

import type {
  SmartSearchPromptLocale,
  SmartSearchPromptSuggestionConfig,
} from '@/types/smartSearchPromptSuggestion';

import { defaultNS } from '@/i18n/i18n';

const SUPPORTED_LOCALES: SmartSearchPromptLocale[] = ['en', 'es'];

const isSupportedLocale = (
  language: string,
): language is SmartSearchPromptLocale =>
  (SUPPORTED_LOCALES as readonly string[]).includes(language);

const normalizeLocale = (language: string): SmartSearchPromptLocale => {
  if (isSupportedLocale(language)) return language;
  return 'en';
};

const nonEmpty = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  return trimmed;
};

const readBundledTranslation = (
  i18n: I18nInstance,
  key: string,
  language: string,
): string | undefined => {
  const locale = normalizeLocale(language);
  const value: unknown = i18n.getResource(locale, defaultNS, key);
  return typeof value === 'string' ? nonEmpty(value) : undefined;
};

/** Validates LD JSON entries before resolving — malformed items are skipped. */
export const parseSmartSearchPromptSuggestionEntry = (
  entry: unknown,
): SmartSearchPromptSuggestionConfig | undefined => {
  if (typeof entry !== 'object' || entry === null) return undefined;

  const { translationKey, defaultTranslations: rawDefaults } = entry as {
    translationKey?: unknown;
    defaultTranslations?: unknown;
  };

  if (typeof translationKey !== 'string') return undefined;

  const trimmedKey = translationKey.trim();
  if (!trimmedKey) return undefined;

  const defaultTranslations: Partial<Record<SmartSearchPromptLocale, string>> =
    {};

  if (typeof rawDefaults === 'object' && rawDefaults !== null) {
    for (const locale of SUPPORTED_LOCALES) {
      const value = (rawDefaults as Record<string, unknown>)[locale];
      if (typeof value === 'string') {
        defaultTranslations[locale] = value;
      }
    }
  }

  return { translationKey: trimmedKey, defaultTranslations };
};

export const resolveSmartSearchPromptText = (
  entry: SmartSearchPromptSuggestionConfig,
  i18n: I18nInstance,
  language: string,
): string | undefined => {
  const key = entry.translationKey.trim();
  if (!key) return undefined;

  if (i18n.exists(key)) {
    const translated = readBundledTranslation(i18n, key, language);
    if (translated) return translated;
  }

  const locale = normalizeLocale(language);
  const { defaultTranslations } = entry;

  return (
    nonEmpty(defaultTranslations[locale]) ??
    nonEmpty(defaultTranslations.en) ??
    nonEmpty(defaultTranslations.es)
  );
};
